/* NPM */
import AlertIcon from '@mdi/svg/svg/alert.svg?react';
import classNames from 'classnames';
import React from 'react';
/* COMPONENT LIB */
import { Button, MDIIcon } from '@solinkcorp/solink-components';
/* SASS */
import './CloudStatus.sass';
/* ICONS */
import AlertCircle from '@mdi/svg/svg/alert-circle.svg?react';
import AlertOctagon from '@mdi/svg/svg/alert-octagon.svg?react';
import CloudCheck from '@mdi/svg/svg/cloud-check.svg?react';
import CloudSync from '@mdi/svg/svg/cloud-sync.svg?react';
import CloudEmailNotSent from '@solinkcorp/solink-components/svg/email-not-sent.svg?react';
/* ICONS */
import { type IEvent } from 'clients/PlatformApi';
import { type CloudStatusBadgeKey, useCloudStatusBadges } from './useCloudStatusBadges';

interface CloudStatusProps {
  eventStatus?: string;
  pollingStatus?: string | null;
  isInProgress?: boolean;
  locationId?: string;
  event: IEvent;
  tenantId: string;
  colorMode?: 'light' | 'dark';
}

/** The Legacy look of each badge. The keys come from `useCloudStatusBadges`. */
const BADGE_STYLES: Record<
  CloudStatusBadgeKey,
  {
    className: string;
    dataTestId: string;
    fill?: string;
    icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  }
> = {
  deviceOffline: {
    className: 'cloud-status-button device-offline-button',
    dataTestId: 'cloud-status-device-offline',
    fill: 'red',
    icon: AlertIcon,
  },
  done: {
    className: 'cloud-status-button',
    dataTestId: 'cloud-status-done',
    icon: CloudCheck,
  },
  error: {
    className: 'cloud-status-button cloud-status-error',
    dataTestId: 'cloud-status-error',
    fill: 'var(--red-1)',
    icon: AlertOctagon,
  },
  verify: {
    className: 'cloud-status-button',
    dataTestId: 'cloud-status-verify',
    fill: 'var(--orange-1)',
    icon: CloudEmailNotSent,
  },
  doneWithErrors: {
    className: 'cloud-status-button cloud-status-done-with-errors',
    dataTestId: 'cloud-status-done-with-errors',
    fill: 'var(--orange-1)',
    icon: CloudCheck,
  },
  uploading: {
    className: 'cloud-status-button',
    dataTestId: 'cloud-status-uploading',
    icon: CloudSync,
  },
};

// TODO locationId here is from events, so will be deviceId effectively (events will get a siteId in future)
const CloudStatus: React.FC<CloudStatusProps> = (props) => {
  const { isInProgress, pollingStatus, eventStatus, locationId, event, tenantId, colorMode } =
    props;

  const { badges, cloudStatus } = useCloudStatusBadges({
    event,
    eventStatus,
    isInProgress,
    locationId,
    pollingStatus,
    tenantId,
  });

  const showDeviceOfflineAlert = badges.some((badge) => badge.key === 'deviceOffline');
  const parentClass = classNames({
    'multi-statuses': showDeviceOfflineAlert,
  });

  if (!cloudStatus) {
    return null;
  }

  return (
    <div id="cloud-status" className={parentClass}>
      {badges.map((badge) => {
        const style = BADGE_STYLES[badge.key];
        return (
          <div className="status-container" key={badge.key}>
            {badge.key === 'doneWithErrors' && (
              <MDIIcon
                className="status-icon"
                icon={AlertCircle}
                fill="var(--orange-1)"
                size={14}
              />
            )}
            <Button
              dataTestId={style.dataTestId}
              className={style.className}
              icon={<MDIIcon icon={style.icon} fill={style.fill} />}
              iconOnly={true}
              tooltip={badge.tooltip}
              type="clear"
              colorMode={badge.key === 'deviceOffline' ? 'line-dark-red' : colorMode}
            />
          </div>
        );
      })}
    </div>
  );
};

export default CloudStatus;
